import React from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { BookOpen, FileText, Monitor, Printer, ChevronRight, GraduationCap, Star } from "lucide-react";

/**
 * IELTS Start Hub — first stop after login if the student hasn't picked their format.
 * Asks "Paper-based or Computer-based?" then shows the two main flow cards.
 */

export default function IELTSStart() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const ieltsFormat = user?.ielts_format;

  const setFormat = async (fmt) => {
    try {
      if (fmt === null) {
        await api.post("/auth/ielts-prefs", { clear_format: true, clear_mode: true });
      } else {
        await api.post("/auth/ielts-prefs", { ielts_format: fmt });
      }
      if (refresh) await refresh();
    } catch (e) { /* ignore */ }
  };

  const setMode = async (mode) => {
    try {
      await api.post("/auth/ielts-prefs", { ielts_mode: mode });
      if (refresh) await refresh();
    } catch (e) { /* ignore */ }
    nav(mode === "guided" ? "/app/ielts/guidelines" : "/app/ielts/mock");
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-10 py-4" data-testid="ielts-start-hub">
        {/* Step 1 — pick format */}
        {!ieltsFormat ? (
          <section>
            <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Step 1 of 2</div>
            <h1 className="font-display text-3xl md:text-4xl text-[#003049] mt-2">
              Which IELTS test format are you preparing for?
            </h1>
            <p className="text-slate-600 mt-2">Your choice will tailor the test interface, timing rules and guidance.</p>

            <div className="grid md:grid-cols-2 gap-5 mt-8">
              <button onClick={() => setFormat("paper")} data-testid="format-paper-btn"
                className="group text-left bg-white border-2 border-slate-200 hover:border-[#F77F00] rounded-2xl p-6 hover:shadow-xl transition-all">
                <Printer className="w-10 h-10 text-[#003049]" />
                <div className="font-display text-2xl text-[#003049] mt-4">Paper-Based IELTS</div>
                <div className="text-sm text-slate-600 mt-1">Write answers on a printed paper sheet. 10-minute transfer time at the end of Listening.</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F77F00]">
                  Continue <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </button>
              <button onClick={() => setFormat("computer")} data-testid="format-computer-btn"
                className="group text-left bg-white border-2 border-slate-200 hover:border-[#F77F00] rounded-2xl p-6 hover:shadow-xl transition-all">
                <Monitor className="w-10 h-10 text-[#003049]" />
                <div className="font-display text-2xl text-[#003049] mt-4">Computer-Based IELTS</div>
                <div className="text-sm text-slate-600 mt-1">Answer directly on screen. ~2 minutes review time at the end of Listening. Faster results.</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#F77F00]">
                  Continue <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </button>
            </div>
          </section>
        ) : (
          /* Step 2 — pick mode */
          <section>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-500">
              <span>Step 2 of 2</span>
              <span>·</span>
              <span data-testid="ielts-format-badge">{ieltsFormat === "paper" ? "Paper-based" : "Computer-based"}</span>
              <button onClick={() => setFormat(null)} className="ml-2 text-slate-400 hover:text-[#F77F00]">change</button>
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#003049] mt-2">
              How would you like to practice today?
            </h1>
            <p className="text-slate-600 mt-2">Pick guided learning if you want a teacher-style walkthrough first, or jump straight into a realistic mock test.</p>

            <div className="grid md:grid-cols-2 gap-5 mt-8">
              <button onClick={() => setMode("guided")} data-testid="mode-guided-btn"
                className="group text-left rounded-2xl p-7 text-white relative overflow-hidden shadow-xl hover:-translate-y-1 transition-all"
                style={{ background: "linear-gradient(135deg, #0EA5E9 0%, #003049 100%)" }}>
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
                <GraduationCap className="w-12 h-12" />
                <div className="font-display text-3xl mt-4">Test + Guidelines</div>
                <div className="text-sm text-white/85 mt-2 leading-relaxed">
                  Learn stage-by-stage: format · timing · dos & don'ts · smart strategies. The system walks you through each section like a real IELTS teacher.
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium">
                  Begin guided learning <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </button>

              <button onClick={() => setMode("mock")} data-testid="mode-mock-btn"
                className="group text-left rounded-2xl p-7 text-white relative overflow-hidden shadow-xl hover:-translate-y-1 transition-all"
                style={{ background: "linear-gradient(135deg, #F77F00 0%, #003049 100%)" }}>
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
                <Star className="w-12 h-12" />
                <div className="font-display text-3xl mt-4">Mock Tests</div>
                <div className="text-sm text-white/85 mt-2 leading-relaxed">
                  Skip the guidance and step straight into a realistic exam environment. Timed, scored, and graded like the real {ieltsFormat === "paper" ? "paper-based" : "computer-based"} test.
                </div>
                <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium">
                  Start mock test <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </button>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}
